import { type ReactElement } from "react"
import { useStoryblokState } from "@storyblok/react"
import { QueryClient } from "@tanstack/react-query"
import { type GetServerSidePropsContext, type InferGetServerSidePropsType } from "next"
import { z } from "zod"
import { ReferralSchema } from "@/features/reward-scheme"
import { getStoryblokLayout, type SbReferralStory } from "@/features/storyblok"
import { type StoryblokLayoutProps } from "@/features/storyblok/components/StoryblokLayout"
import { ReferralTemplate } from "@/features/storyblok/components/StoryTemplate/templates/Referral"
import { withStoryProps } from "@/features/storyblok/server"
import { graphql } from "@/graphql"
import { krakenAuth } from "@/lib/blueprint-auth/server"
import { onboardingRoutesPaths } from "@/routes/onboarding"

type ReferralPageProps = InferGetServerSidePropsType<typeof getServerSideProps>

export default function ReferralPage({ story, referral }: ReferralPageProps) {
    const dynamicStory = useStoryblokState<SbReferralStory["content"]>(story)

    if (!dynamicStory) return

    return (
        <ReferralTemplate
            story={dynamicStory}
            referral={referral}
        />
    )
}

ReferralPage.getLayout = (page: ReactElement<StoryblokLayoutProps>) => getStoryblokLayout(page)

ReferralPage.messages = [...ReferralTemplate.messages, ...getStoryblokLayout.messages] as const

const QueryParamsSchema = z
    .object({
        codeParrainage: z.string(),
    })
    .transform((value) => ({
        rewardCode: value.codeParrainage,
    }))

const GetDomesticReferralSchemeQuery = graphql(`
    query getDomesticReferralSchemeQuery($code: String!) {
        domesticAccountReferralRewardScheme(code: $code) {
            referrerGivenName
            referredRewardAmount
            referrerRewardAmount
        }
    }
`)

export async function getServerSideProps(context: GetServerSidePropsContext) {
    const queryClient = new QueryClient()
    const krakenViewerAuth = await krakenAuth.getAuth.viewer({ context })
    const krakenViewerGraphQLClient = krakenAuth.getGraphQLClient.viewer({ auth: krakenViewerAuth })

    const parsedQueryParams = QueryParamsSchema.safeParse(context.query)

    if (!parsedQueryParams.success) {
        return { notFound: true }
    }

    const rewardCode = parsedQueryParams.data.rewardCode

    try {
        const { domesticAccountReferralRewardScheme } = await queryClient.fetchQuery({
            queryKey: ["getReferralScheme", rewardCode],
            async queryFn() {
                return await krakenViewerGraphQLClient.request(GetDomesticReferralSchemeQuery, {
                    code: rewardCode,
                })
            },
        })

        const referral = ReferralSchema.parse({
            ...domesticAccountReferralRewardScheme,
            code: rewardCode,
        })

        return withStoryProps({
            slug: "parrainage",
            options: {
                content_type: "referral",
            },
            context,
            intlNamespaces: ReferralPage.messages,
            queryClient,
            props: { referral },
        })
    } catch {
        return {
            redirect: {
                destination: onboardingRoutesPaths.meterDetails.resolve(),
                permanent: false,
            },
        }
    }
}
